import { getProfileImage } from "@shared/hooks/useProfile";
import React from "react";
import { Resource } from "../types/resource.types";

interface ResourceCardProps {
  resource: Resource;
  onClick: (resourceId: string) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, onClick }) => {
  // Get the first material category to display as a tag
  const firstCategory =
    resource.materials && resource.materials.length > 0
      ? resource.materials[0].category
      : null;

  // Get featured image or first material image
  const featureImageUrl =
    resource.featuredImage && resource.featuredImage.length > 0
      ? resource.featuredImage[0].variants?.[0]?.url
      : resource.materials &&
          resource.materials.length > 0 &&
          resource.materials[0].image &&
          resource.materials[0].image.length > 0
        ? resource.materials[0].image[0].variants?.[0]?.url
        : null;

  return (
    <article
      className="flex bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition h-64"
      onClick={() => onClick(resource.id || "")}
    >
      {/* Image or placeholder */}
      <div className="relative block w-24 sm:w-56 xl:sidebar-expanded:w-40 2xl:sidebar-expanded:w-56 shrink-0">
        {featureImageUrl ? (
          <img
            className="absolute object-cover object-center w-full h-full"
            src={featureImageUrl}
            alt={resource.title}
            loading="lazy"
          />
        ) : (
          <div className="absolute flex flex-col items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-700 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mb-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 7v10a4 4 0 004 4h10a4 4 0 004-4V7a4 4 0 00-4-4H7a4 4 0 00-4 4z"
              />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span className="text-xs">No Image</span>
          </div>
        )}
      </div>
      {/* Content */}
      <div className="grow p-5 flex flex-col">
        <div className="grow">
          <div className="inline-flex mb-2">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {resource.title}
            </h3>
          </div>
          {resource.description && (
            <div
              className="text-sm text-gray-700 dark:text-gray-200 mb-2 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: resource.description }}
            />
          )}
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center mt-3">
          {/* Tag: category */}
          {firstCategory && (
            <div className="text-xs inline-flex items-center font-medium border border-gray-200 dark:border-gray-700/60 text-gray-600 dark:text-gray-400 rounded-full text-center px-2.5 py-1">
              <span>{firstCategory}</span>
            </div>
          )}
          {/* Author */}
          {resource.author && (
            <div className="flex items-center space-x-2">
              <div className="flex">
                <img
                  className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 box-content object-cover"
                  src={getProfileImage(resource.author) || undefined}
                  alt={resource.author?.name || "Resource author"}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ResourceCard;
